import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';
import { campaignExpensesRepository } from '../repositories/campaignExpenses.repository';
import { campaignKeys } from '../services/campaign.keys';
import type { CampaignExpense } from '../types/campaign.types';

export type { CampaignExpense };

export const useCampaignExpenses = (campaignId: string) => {
  const { currentOrganization } = useOrganization();

  return useQuery({
    queryKey: campaignKeys.expenses(campaignId, currentOrganization?.id),
    queryFn: async () => {
      if (!currentOrganization?.id) throw new Error('No organization selected');
      return campaignExpensesRepository.getExpenses(campaignId, currentOrganization.id);
    },
    enabled: !!campaignId && !!currentOrganization?.id,
  });
};

export const useAddCampaignExpense = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async (expense: Omit<CampaignExpense, 'id' | 'created_at' | 'updated_at'>) => {
      if (!currentOrganization?.id) throw new Error('No organization selected');
      return campaignExpensesRepository.addExpense(expense, currentOrganization.id);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: campaignKeys.expenses(variables.campaign_id, currentOrganization?.id),
      });
      queryClient.invalidateQueries({ queryKey: campaignKeys.budget(variables.campaign_id) });
      toast({
        title: 'Success',
        description: 'Expense added successfully',
      });
    },
    onError: (error) => {
      console.error('Add expense error:', error);
      toast({
        title: 'Error',
        description: 'Failed to add expense',
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteCampaignExpense = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({ expenseId, campaignId }: { expenseId: string; campaignId: string }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected');
      await campaignExpensesRepository.deleteExpense(expenseId, currentOrganization.id);
      return { expenseId, campaignId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: campaignKeys.expenses(data.campaignId, currentOrganization?.id),
      });
      queryClient.invalidateQueries({ queryKey: campaignKeys.budget(data.campaignId) });
      toast({
        title: 'Success',
        description: 'Expense deleted successfully',
      });
    },
    onError: (error) => {
      console.error('Delete expense error:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete expense',
        variant: 'destructive',
      });
    },
  });
};
